import { CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { RightPanelSettings } from '@common-modules/shared/navigation/right-panel-settings';
import { RightPanelService } from '@common-modules/shared/navigation/right-panel.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MainLayoutService } from '../main-layout.service';

const COMPONENT_SELECTOR = 'common-main-layout';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './main-layout.component.html',
    styleUrls: ['./main-layout.component.scss'],
    standalone: false
})
export class MainLayoutComponent implements OnInit {
  @ContentChild('topBar') topBarRef: TemplateRef<any>;
  @ContentChild('navBar') navBarRef: TemplateRef<any>;

  @Input() configLoaded = false;

  private sizeWatching: Set<any> = new Set<any>();

  hoverEnabled = false;
  hideTopBar = false;

  constructor(
    private readonly _rightPanelService: RightPanelService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _mainLayoutService: MainLayoutService
  ) {
    this._mainLayoutService.hideTopBar$
      .pipe(untilDestroyed(this))
      .subscribe((hideTopBar) => (this.hideTopBar = hideTopBar));

    this.rightPanelWidthFx = 0;
    this.rightPanelCollapsed = this._rightPanelService.collapsedObservable();
    this.rightPanelHover = this._rightPanelService.HoverObservable();

    this.rightPanelHover.pipe(untilDestroyed(this)).subscribe((hover) => {
      this.hoverEnabled = hover;
      if (hover) {
        this.mainContentWidthFx = 100;
      }
    });

    this.rightPanelOverlay = this._rightPanelService.overlayObservable().pipe(
      map((value) => {
        if (value === 'over') {
          this.detectScrollbarWidth();
        }

        return value;
      })
    );

    this._rightPanelService.rightPanelInitialize.pipe(untilDestroyed(this)).subscribe((size) => {
      this.rightPanelLastWidth = size?.widthFx;
    });

    this.rightPanelVisible = this._rightPanelService.visibilityObservable().pipe(
      map((value) => {
        this.rightPanelVisibility = value;
        this.setPanelProportions();
        return value;
      })
    );

    this.resizeObserver();
  }

  public rightPanelVisible: Observable<boolean>;
  public rightPanelCollapsed: Observable<boolean>;
  public rightPanelHover: Observable<boolean>;
  public rightPanelOverlay: Observable<string>;

  private rightPanelVisibility: boolean;
  private _rightPanelWidthFx: number;
  public get rightPanelWidthFx(): number {
    return this._rightPanelWidthFx;
  }
  public set rightPanelWidthFx(value: number) {
    this._rightPanelWidthFx = value;
    if (this.hoverEnabled || !this.rightPanelVisibility) {
      this._mainContentWidthFx = 100;
    } else {
      this._mainContentWidthFx = 100 - value;
    }
  }

  private _mainContentWidthFx: number;
  public get mainContentWidthFx(): number {
    return this._mainContentWidthFx;
  }
  public set mainContentWidthFx(value: number) {
    this._mainContentWidthFx = value;
    if (!this.hoverEnabled) {
      this._rightPanelWidthFx = 100 - value;
    }
  }

  private _rightPanelLastWidth;
  public get rightPanelLastWidth() {
    return this._rightPanelLastWidth;
  }
  public set rightPanelLastWidth(value) {
    this._rightPanelLastWidth = value;
    this.setPanelProportions();
  }

  public dragPosition = { x: 0, y: 0 };

  private defaultRightPanelWidthPixel = 550;

  private rightPanelProportion: number;
  scrollBarCss: string;
  private rightPanelInitialize$: Observable<RightPanelSettings>;
  private rightPanelVisibility$: Observable<boolean>;

  private setPanelProportions(): void {
    if (this.rightPanelVisibility === false) {
      this.mainContentWidthFx = 100;
    } else {
      this.rightPanelWidthFx = this.rightPanelLastWidth;
    }
  }

  private detectScrollbarWidth() {
    const item = document.getElementById('router-content');
    const scrollbarWidth = item.offsetWidth - item.clientWidth;
    this.scrollBarCss = scrollbarWidth + 1 + 'px';
  }

  dragEnded() {
    this.rightPanelLastWidth = this.rightPanelWidthFx;
  }

  dragMove(dragHandle: HTMLElement, $event: CdkDragMove<any>) {
    // TODO this isn't best practice in Angular, but works for now
    const x = dragHandle.parentNode.parentNode;
    const y = x['getBoundingClientRect']();
    const z = y['width'];

    if (this.rightPanelLastWidth == null) {
      this.rightPanelLastWidth = z;
    }

    const newWidth = this.rightPanelLastWidth + $event.distance.x * -1;

    this.rightPanelWidthFx = newWidth;

    x['style']['width'] = newWidth + 'px';
  }

  ngAfterContentChecked(): void {
    this._cd.detectChanges();
  }

  ngOnInit() {
    this.resetRightPanelSize();

    this.rightPanelInitialize$ = this._rightPanelService.rightPanelInitialize.asObservable();
    this.rightPanelInitialize$.pipe(untilDestroyed(this)).subscribe((descriptor) => {
      if (descriptor) {
        this.rightPanelProportion = descriptor.proportion;
        this.rightPanelWidthFx = descriptor.widthFx;
      } else {
        this.resetRightPanelSize();
      }
    });

    this.rightPanelVisibility$ = this._rightPanelService.hasRightPanelObservable();
    this.rightPanelVisibility$.pipe(untilDestroyed(this)).subscribe((visibility) => {
      this._rightPanelService.setVisible(visibility);
      if (!visibility) {
        this.rightPanelWidthFx = 0;
      }
    });

    const rightPanelSizeChanges$ = this._rightPanelService.rightPanelSizeChanges.asObservable();
    rightPanelSizeChanges$.pipe(untilDestroyed(this)).subscribe((settings) => {
      if (settings) {
        this.rightPanelWidthFx = settings.widthFx;
      }
    });
  }

  dragEnded2($event: CdkDragEnd<any>) {
    const x = $event.source.getRootElement().getBoundingClientRect().x;
    const w = window.innerWidth;

    this.rightPanelProportion = (w - x) / w;
    this.rightPanelWidthFx = this.rightPanelProportion * 100;
    this.setTransform();

    this._rightPanelService.setResized({
      proportion: this.rightPanelProportion,
      widthFx: this.rightPanelWidthFx,
    } as RightPanelSettings);

    this.rightPanelLastWidth = this.rightPanelWidthFx;
  }

  setTransform() {
    this.dragPosition = { x: 0, y: 0 };
  }

  private resetRightPanelSize() {
    this.rightPanelProportion = this.defaultRightPanelWidthPixel / window.innerWidth;
    this.rightPanelWidthFx = this.rightPanelProportion * 100;
  }

  private resizeObserver() {
    // Only run if ResizeObserver is supported.
    if ('ResizeObserver' in self) {
      // Create a single ResizeObserver instance to handle all
      // container elements. The instance is created with a callback,
      // which is invoked as soon as an element is observed as well
      // as any time that element's size changes.
      var ro = new ResizeObserver(function (entries) {
        // Default breakpoints that should apply to all observed
        // elements that don't define their own custom breakpoints.
        var defaultBreakpoints = { SM: 384, MD: 576, LG: 768, XL: 960 };

        entries.forEach(function (entry) {
          // If breakpoints are defined on the observed element,
          // use them. Otherwise use the defaults.
          var breakpoints = entry.target['dataset']?.breakpoints
            ? JSON.parse(entry.target['dataset'].breakpoints)
            : defaultBreakpoints;

          // Update the matching breakpoints on the observed element.
          Object.keys(breakpoints).forEach(function (breakpoint) {
            var minWidth = breakpoints[breakpoint];
            if (entry.contentRect.width >= minWidth) {
              entry.target.classList.add(breakpoint);
            } else {
              entry.target.classList.remove(breakpoint);
            }
          });
        });
      });

      setInterval(() => this.resizeWatch(ro), 1000);
    } else {
      console.log('No Resize');
    }
  }

  private resizeWatch(ro: ResizeObserver) {
    var elements = document.querySelectorAll('[data-observe-resizes]');
    for (var element, i = 0; (element = elements[i]); i++) {
      if (this.sizeWatching.has(element)) {
      } else {
        ro.observe(element);
        this.sizeWatching.add(element);
      }
    }
  }

  get componentName() {
    return 'AppComponent';
  }
}
